import axios from 'axios'
import ConfigConstants from '@/constants/Config.constants'
import { Response, SnaphotReference } from '@/models'
import { ICustomMetricService } from '../ICustomMetricService'
import {
  CustomMetric,
  CustomMetricPreview,
  Query,
  Tool,
  Toolbox,
} from '@/models/custom-metric.model'

export default class CustomMetricService implements ICustomMetricService {
  private endpointUrl: string

  private readonly options = {
    headers: {
      'Content-Type': ConfigConstants.CONTENT_TYPES.YT_V1,
      Accept: '*/*',
    },
  } as const

  constructor(endpointUrl: string) {
    this.endpointUrl = endpointUrl
  }

  public async getInitialToolbox(projectId: number): Promise<Toolbox> {
    return axios
      .get<Response<Toolbox>>(
        `${this.endpointUrl}/project/${projectId}/custom-metric-toolbox/v2`,
        this.options,
      )
      .then((response) => response.data.data)
      .catch((e) => {
        console.error(`Getting the toolbox failed: ${JSON.stringify(e)}`)
        return {}
      })
  }

  public async getToolbox(
    projectId: number,
    toolbox: Toolbox,
    query?: string,
    contextUUID?: string,
  ): Promise<Toolbox> {
    let delim = '?'
    let params = ''
    if (query) {
      params += delim + 'query=' + query
      delim = '&'
    }
    if (contextUUID) {
      params += delim + 'context=' + contextUUID
      delim = '&'
    }
    params += delim + 'pageSize=20'
    delim = '&'
    if (!toolbox.query) {
      return axios
        .get<Response<Toolbox>>(
          `${this.endpointUrl}/project/${projectId}/custom-metric-toolbox/v2${params}`,
          this.options,
        )
        .then((response) => ({
          ...response.data.data,
          toolsCount: response.data.pagination?.totalElements || response.data.data.tools?.length,
        }))
        .catch((e) => {
          console.error(`Getting the toolbox failed: ${JSON.stringify(e)}`)
          return {}
        })
    } else {
      return axios
        .post<Response<Toolbox>>(
          `${this.endpointUrl}/project/${projectId}/custom-metric-toolbox/v2${params}`,
          { data: toolbox },
          this.options,
        )
        .then((response) => ({
          ...response.data.data,
          toolsCount: response.data.pagination?.totalElements || response.data.data.tools?.length,
        }))
        .catch((e) => {
          console.error(`Getting the toolbox for query failed: ${JSON.stringify(e)}`)
          return {}
        })
    }
  }

  public async applyTool(projectId: number, toolbox: Toolbox, tool: Tool): Promise<Toolbox> {
    const location = tool.targets[0]
    return axios
      .post<
        Response<Toolbox>
      >(`${this.endpointUrl}/project/${projectId}/custom-metric-toolbox/v2/apply`, { data: { ...toolbox, location, tools: undefined, tool } }, this.options)
      .then((response) => ({
        ...response.data.data,
        toolsCount: response.data.pagination?.totalElements || response.data.data.tools?.length,
      }))
  }

  public async getCustomMetrics(projectId: number): Promise<CustomMetric[]> {
    return axios
      .get<
        Response<CustomMetric[]>
      >(`${this.endpointUrl}/project/${projectId}/custom-metric`, this.options)
      .then((response) => response.data.data)
  }

  public async createCustomMetric(
    projectId: number,
    customMetric: Omit<CustomMetric, 'id'>,
  ): Promise<CustomMetric> {
    return axios
      .post<
        Response<CustomMetric>
      >(`${this.endpointUrl}/project/${projectId}/custom-metric`, { data: customMetric }, this.options)
      .then((response) => response.data.data)
  }

  public async getCustomMetricsById(
    projectId: number,
    customMetricId: number,
  ): Promise<CustomMetric | undefined> {
    return axios
      .get<
        Response<CustomMetric>
      >(`${this.endpointUrl}/project/${projectId}/custom-metric/${customMetricId}`, this.options)
      .then((response) => response.data.data)
  }

  public async updateCustomMetric(
    projectId: number,
    customMetric: CustomMetric,
  ): Promise<CustomMetric> {
    return axios
      .put<
        Response<CustomMetric>
      >(`${this.endpointUrl}/project/${projectId}/custom-metric/${customMetric.id}`, { data: customMetric }, this.options)
      .then((response) => response.data.data)
  }

  public async deleteCustomMetric(
    projectId: number,
    customMetricId: number,
  ): Promise<CustomMetric> {
    return axios
      .delete<
        Response<CustomMetric>
      >(`${this.endpointUrl}/project/${projectId}/custom-metric/${customMetricId}`, this.options)
      .then((response) => response.data.data)
  }

  public async getCustomMetricPreview(
    projectId: number,
    snapshotId: SnaphotReference,
    query: Query,
  ): Promise<CustomMetricPreview> {
    const url = `${this.endpointUrl}/project/${projectId}/snapshot/${snapshotId}/custom-metric-preview-query`
    return axios
      .post<Response<CustomMetricPreview>>(url, { data: query }, this.options)
      .then((response) => response.data.data)
      .catch(() => {
        return {
          columns: [],
          rows: [],
        }
      })
  }
}
